import { useQuery, keepPreviousData as previousPlaceholder } from '@tanstack/react-query';
import isArray from 'lodash-es/isArray';

import { ApiDocumentsRecommendationResponse, ApiDocumentsResponse } from '../lib/api/Documents';

import { useApi } from '../contexts/ApiContext';

type UseDocumentsOptions = Omit<
    Parameters<typeof useQuery<ApiDocumentsResponse>>[0],
    'queryKey' | 'queryFn'
> & {
    keepPreviousData?: boolean;
};

type UseDocumentsRecommendationsOptions = Omit<
    Parameters<typeof useQuery<ApiDocumentsRecommendationResponse>>[0],
    'queryKey' | 'queryFn'
> & {
    keepPreviousData?: boolean;
};

export function useRelatedDocuments(query = null, opts: UseDocumentsRecommendationsOptions = {}) {
    const api = useApi();
    const { keepPreviousData = true, ...otherOpts } = opts || {};
    const { id, ...relatedQuery } = query || {};
    const { data = null, ...queryResult } = useQuery<ApiDocumentsRecommendationResponse>({
        queryKey: ['document_related', id, relatedQuery],
        queryFn: ({ queryKey: [, related, queryParam], signal }) =>
            api.documents.related(related, queryParam, { signal }),
        placeholderData: keepPreviousData ? previousPlaceholder : null,
        ...otherOpts,
    });

    const { data: items = null, ...metadata } = isArray(data) ? { data } : data || {};

    return data !== null
        ? {
              items,
              ...metadata,
              ...queryResult,
          }
        : queryResult;
}

export function useNextDocuments(query = null, opts: UseDocumentsRecommendationsOptions = {}) {
    const api = useApi();
    const { keepPreviousData = true, ...otherOpts } = opts || {};
    const { id, ...relatedQuery } = query || {};
    const { data = null, ...queryResult } = useQuery({
        queryKey: ['document_next', id, relatedQuery],
        queryFn: ({ queryKey: [, related, queryParam], signal }) =>
            api.documents.next(related, queryParam, { signal }),
        placeholderData: keepPreviousData ? previousPlaceholder : null,
        ...otherOpts,
    });

    const { data: items = null, ...metadata } = isArray(data) ? { data } : data || {};

    return data !== null
        ? {
              items,
              ...metadata,
              ...queryResult,
          }
        : queryResult;
}

export function usePopularDocuments(query = null, opts: UseDocumentsRecommendationsOptions = {}) {
    const api = useApi();
    const { keepPreviousData = true, ...otherOpts } = opts || {};
    const { data = null, ...queryResult } = useQuery({
        queryKey: ['document_popular', query],
        queryFn: ({ queryKey: [, queryParam], signal }) =>
            api.documents.popular(queryParam, { signal }),
        suspense: false,
        placeholderData: keepPreviousData ? previousPlaceholder : null,
        ...otherOpts,
    });

    const { data: items = null, ...metadata } = isArray(data) ? { data } : data || {};

    return data !== null
        ? {
              items,
              ...metadata,
              ...queryResult,
          }
        : queryResult;
}

export function useContinueDocuments(query = null, opts: UseDocumentsRecommendationsOptions = {}) {
    const api = useApi();
    const { keepPreviousData = true, ...otherOpts } = opts || {};
    const { data = null, ...queryResult } = useQuery({
        queryKey: ['document_continue', query],
        queryFn: ({ queryKey: [, queryParam], signal }) =>
            api.documents.continue(queryParam, { signal }),
        placeholderData: keepPreviousData ? previousPlaceholder : null,
        ...otherOpts,
    });

    const { data: items = null, ...metadata } = isArray(data) ? { data } : data || {};

    return data !== null
        ? {
              items,
              ...metadata,
              ...queryResult,
          }
        : queryResult;
}

export function fetchDocuments(queryClient, api, query) {
    return queryClient.fetchQuery({
        queryKey: ['documents', query],
        queryFn: ({ queryKey: [, queryParam], signal }) =>
            api.documents.get(queryParam, null, null, { signal }),
    });
}

export default function useDocuments(query = null, opts: UseDocumentsOptions = {}) {
    const api = useApi();
    const { keepPreviousData = true } = opts || {};
    const { data = null, ...queryResult } = useQuery({
        queryKey: ['documents', query],
        queryFn: ({ queryKey: [, queryParam], signal }) =>
            api.documents.get(queryParam, null, null, { signal }),
        placeholderData: keepPreviousData ? previousPlaceholder : null,
        ...opts,
    });

    const { data: items = null, ...metadata } = isArray(data) ? { data } : data || {};

    return {
        items,
        ...metadata,
        ...queryResult,
    };
}
